import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputBase,
  Typography,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  OutlinedInput,
} from "@mui/material";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import API from "../api";

const FindJob = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [country, setCountry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [responseError, setResponseError] = useState(null);
  const [providerRoles, setProviderRoles] = useState([]);
  const [availableSpecialties, setAvailableSpecialties] = useState([]);
  const [selectedSpecialties, setSelectedSpecialties] = useState([]);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "olivia@untitledui.com",
    provider_role_id: "",
    board_certified: "1",
    board_eligible: "1",
  });

  const [errors, setErrors] = useState({
    name: false,
    phone: false,
    email: false,
    provider_role_id: false,
    board_certified: false,
    board_eligible: false,
  });

  const handleInputChange = (e) => {
    setResponseError(null);
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    // Handle different field types for validation
    let isValid = false;
    if (
      name === "provider_role_id" ||
      name === "board_certified" ||
      name === "board_eligible"
    ) {
      // For dropdown fields, check if value is selected
      isValid = value !== "";
    } else {
      // For text fields, check if trimmed value is not empty
      isValid = value.trim() !== "";
    }

    setErrors((prevState) => ({
      ...prevState,
      [name]: !isValid,
    }));
  };

  const handlePhoneChange = (value) => {
    setResponseError(null);
    setFormData((prevState) => ({
      ...prevState,
      phone: value,
    }));

    setErrors((prevState) => ({
      ...prevState,
      phone: !value,
    }));
  };

  const handleSpecialtyChange = (event) => {
    setResponseError(null);
    const value = event.target.value;
    if (typeof value === "string") {
      setSelectedSpecialties(value.split(","));
    } else {
      setSelectedSpecialties(value);
    }
  };

  const handleRemoveSpecialty = (specialtyToRemove) => {
    setSelectedSpecialties(
      selectedSpecialties.filter((specialty) => specialty !== specialtyToRemove)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResponseError(null);
    const newErrors = {
      name: formData.name.trim() === "",
      phone: !formData.phone,
      email: formData.email.trim() === "",
      provider_role_id: formData.provider_role_id === "",
      board_certified: formData.board_certified === "",
      board_eligible: formData.board_eligible === "",
    };

    setErrors(newErrors);

    const isFormValid = Object.values(newErrors).every((error) => !error);

    if (isFormValid && selectedSpecialties.length > 0) {
      setIsLoading(true);
      try {
        const formObj = new FormData();
        formObj.append("name", formData.name.trim());
        formObj.append("phone", formData.phone);
        formObj.append("email", formData.email.trim());
        formObj.append("provider_role_id", formData.provider_role_id);
        formObj.append(
          "speciality_ids",
          selectedSpecialties.map((s) => s.id).join(",")
        );
        formObj.append("board_certified", formData.board_certified);
        formObj.append("board_eligible", formData.board_eligible);

        const resp = await API.post("/api/lead-provider", formObj);
        
        if (resp?.data?.success) {
          setSnackbarMessage("Provider registered successfully!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          
          // Reset form
          setFormData({
            name: "",
            phone: "",
            email: "olivia@untitledui.com",
            provider_role_id: "",
            board_certified: "1",
            board_eligible: "1",
          });
          setSelectedSpecialties([]);
        } else {
          setResponseError(resp?.data?.msg || "Registration failed");
          setSnackbarMessage(resp?.data?.msg || "Registration failed");
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
        
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        const errorMsg = error?.response?.data?.msg || "An error occurred. Please try again.";
        setResponseError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        console.log(error);
      }
    }
  };

  useEffect(() => {
    const getLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
            )
              .then((response) => response.json())
              .then((data) => {
                setCountry(data.countryCode);
                setLoading(false);
              })
              .catch((err) => {
                setError("Failed to fetch country data");
                setLoading(false);
              });
          },
          (error) => {
            setError("Failed to get location");
            setLoading(false);
          }
        );
      } else {
        setError("Geolocation is not supported by this browser.");
        setLoading(false);
      }
    };

    getLocation();
  }, []);

  useEffect(() => {
    API.get("/api/get-provider-roles")
      .then((resp) => {
        if (resp?.data?.success && Array.isArray(resp.data.data)) {
          setProviderRoles(resp.data.data);
        }
      })
      .catch((err) => console.log("Error fetching provider roles:", err));

    API.get("/api/get-medical-specialities")
      .then((resp) => {
        if (resp?.data?.success && Array.isArray(resp.data.data)) {
          setAvailableSpecialties(resp.data.data);
        }
      })
      .catch((err) => console.log("Error fetching specialties:", err));
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F0F4F8" }}>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Grid container sx={{ minHeight: "100vh" }}>
        {/* Left Column - Image */}
        <Grid
          item
          xs={12}
          md={7}
          sx={{
            display: { xs: "none", md: "flex" },
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
            src="https://projects.tangiblethemes.com/Shglocums/wp-content/uploads/2025/08/Find-Job.jpg"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
          {/* Text Overlay */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              background: "linear-gradient(to bottom, transparent, #21404B99)",
              p: 4,
              color: "white",
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 600,
                fontSize: { xs: "24px", xl: "36px" },
                lineHeight: { xs: "32px", xl: "44px" },
                mb: 2,
                maxWidth: "500px",
              }}
            >
            Discover Career Opportunities That Match Your Skills
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: "16px", xl: "20px" },
                lineHeight: { xs: "24px", xl: "30px" },
                opacity: 0.9,
              }}
            >
             Submit your details and let us connect you with top employers. 
  Whether you're just starting out or looking for a change, 
  we help you find jobs that align with your goals and experience.
            </Typography>
          </Box>
        </Grid>

        {/* Right Column - Form */}
        <Grid
          item
          xs={12}
          md={5}
          sx={{
            display: "flex",
            flexDirection: "column",
            bgcolor: "white",
            p: { xs: 2, md: 4 },
            justifyContent:'start',
            alignItems:'start',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "2rem", xl: "2.5rem" },
              fontFamily: "DM Serif Text",
              color: "#002357",
              mb: 4,
              textAlign: "center",
            }}
          >
            Find Job
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width:'100%',
            }}
          >
            {/* Full Name */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "24px",
                  color: "#292A2F",
                  mb: 1,
                }}
              >
                Full Name <span style={{ color: "red" }}>*</span>
              </Typography>
              <InputBase
                fullWidth
                placeholder="Name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                sx={{
                  bgcolor: "#F5F5F5",
                  border: errors.name && "1px solid red !important",
                  height: "46px",
                  borderRadius: "8px",
                  fontWeight: 400,
                  fontSize: "15px",
                  p: "8px 12px 8px 16px",
                  "&:hover": {
                    border: "1px solid #2a9d8f !important",
                  },
                }}
              />
              {errors.name && (
                <span style={{ color: "red", fontSize: "0.7rem" }}>
                  Full name is required
                </span>
              )}
            </Box>

            {/* Phone Number */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "24px",
                  color: "#292A2F",
                  mb: 1,
                }}
              >
                Phone number <span style={{ color: "red" }}>*</span>
              </Typography>
              <PhoneInput
                international
                placeholder="000-0000"
                value={formData.phone}
                onChange={handlePhoneChange}
                style={{
                  paddingLeft: "1rem",
                  borderRadius: "8px",
                  height: "46px",
                  color: "black",
                  background: "#F5F5F5",
                }}
                className="phone-input"
                defaultCountry={country}
              />
              {errors.phone && (
                <span style={{ color: "red", fontSize: "0.7rem" }}>
                  Phone number is required
                </span>
              )}
            </Box>

            {/* Email Address */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "24px",
                  color: "#292A2F",
                  mb: 1,
                }}
              >
                Email Address <span style={{ color: "red" }}>*</span>
              </Typography>
              <InputBase
                fullWidth
                placeholder="olivia@untitledui.com"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                sx={{
                  bgcolor: "#F5F5F5",
                  border: errors.email && "1px solid red !important",
                  height: "46px",
                  borderRadius: "8px",
                  fontWeight: 400,
                  fontSize: "15px",
                  p: "8px 12px 8px 16px",
                  "&:hover": {
                    border: "1px solid #2a9d8f !important",
                  },
                }}
              />
              {errors.email && (
                <span style={{ color: "red", fontSize: "0.7rem" }}>
                  Email is required
                </span>
              )}
            </Box>

            {/* Provider Role */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "24px",
                  color: "#292A2F",
                  mb: 1,
                }}
              >
                Provider Role <span style={{ color: "red" }}>*</span>
              </Typography>
              <FormControl fullWidth>
                <Select
                  name="provider_role_id"
                  value={formData.provider_role_id}
                  onChange={handleInputChange}
                  error={errors.provider_role_id}
                  sx={{
                    height: "46px",
                    borderRadius: "8px",
                    fontWeight: 400,
                    bgcolor: "#F5F5F5",
                    fontSize: "15px",
                    "&:hover": {
                      border: "none",
                      outline: "none",
                    },
                    "&.Mui-focused": {
                      border: "1px solid #2a9d8f",
                      outline: "none",
                    },
                    "& fieldset": {
                      border: "none",
                    },
                  }}
                >
                  <MenuItem value="">Provider Role</MenuItem>
                  {providerRoles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.provider_role_id && (
                  <span style={{ color: "red", fontSize: "0.7rem" }}>
                    Provider role is required
                  </span>
                )}
              </FormControl>
            </Box>

            {/* Specialty */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "24px",
                  color: "#292A2F",
                  mb: 1,
                }}
              >
                Specialty <span style={{ color: "red" }}>*</span>
              </Typography>
              <FormControl fullWidth>
                <Select
                  multiple
                  value={selectedSpecialties}
                  onChange={handleSpecialtyChange}
                  input={<OutlinedInput />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((specialty) => (
                        <Chip
                          key={specialty.id}
                          label={specialty.name}
                          onDelete={() => handleRemoveSpecialty(specialty)}
                          size="small"
                          sx={{
                            bgcolor: "#E9C369",
                            color: "#292A2F",
                            "& .MuiChip-deleteIcon": {
                              color: "#292A2F",
                            },
                          }}
                        />
                      ))}
                    </Box>
                  )}
                  sx={{
                    height: "auto",
                    minHeight: "46px",
                    borderRadius: "8px",
                    fontWeight: 400,
                    bgcolor: "#F5F5F5",
                    fontSize: "15px",
                    "&:hover": {
                      border: "none",
                      outline: "none",
                    },
                    "&.Mui-focused": {
                      border: "1px solid #2a9d8f",
                      outline: "none",
                    },
                    "& fieldset": {
                      border: "none",
                    },
                  }}
                >
                  {availableSpecialties.map((specialty) => (
                    <MenuItem key={specialty.id} value={specialty}>
                      {specialty.name}
                    </MenuItem>
                  ))}
                </Select>
                {selectedSpecialties.length === 0 && (
                  <span style={{ color: "red", fontSize: "0.7rem" }}>
                    At least one specialty is required
                  </span>
                )}
              </FormControl>
            </Box>

            {/* Board Certified */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "24px",
                  color: "#292A2F",
                  mb: 1,
                }}
              >
                Board Certified <span style={{ color: "red" }}>*</span>
              </Typography>
              <FormControl fullWidth>
                <Select
                  name="board_certified"
                  value={formData.board_certified}
                  onChange={handleInputChange}
                  error={errors.board_certified}
                  sx={{
                    height: "46px",
                    borderRadius: "8px",
                    fontWeight: 400,
                    bgcolor: "#F5F5F5",
                    fontSize: "15px",
                    "&:hover": {
                      border: "none",
                      outline: "none",
                    },
                    "&.Mui-focused": {
                      border: "1px solid #2a9d8f",
                      outline: "none",
                    },
                    "& fieldset": {
                      border: "none",
                    },
                  }}
                >
                  <MenuItem value="">Select Board</MenuItem>
                  <MenuItem value="1">Yes</MenuItem>
                  <MenuItem value="0">No</MenuItem>
                </Select>
                {errors.board_certified && (
                  <span style={{ color: "red", fontSize: "0.7rem" }}>
                    Board certified selection is required
                  </span>
                )}
              </FormControl>
            </Box>

            {/* Board Eligible */}
            <Box>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "24px",
                  color: "#292A2F",
                  mb: 1,
                }}
              >
                Board Eligible <span style={{ color: "red" }}>*</span>
              </Typography>
              <FormControl fullWidth>
                <Select
                  name="board_eligible"
                  value={formData.board_eligible}
                  onChange={handleInputChange}
                  error={errors.board_eligible}
                  sx={{
                    height: "46px",
                    borderRadius: "8px",
                    fontWeight: 400,
                    bgcolor: "#F5F5F5",
                    fontSize: "15px",
                    "&:hover": {
                      border: "none",
                      outline: "none",
                    },
                    "&.Mui-focused": {
                      border: "1px solid #2a9d8f",
                      outline: "none",
                    },
                    "& fieldset": {
                      border: "none",
                    },
                  }}
                >
                  <MenuItem value="">Select Board</MenuItem>
                  <MenuItem value="1">Yes</MenuItem>
                  <MenuItem value="0">No</MenuItem>
                </Select>
                {errors.board_eligible && (
                  <span style={{ color: "red", fontSize: "0.7rem" }}>
                    Board eligible selection is required
                  </span>
                )}
              </FormControl>
            </Box>

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{
                mt: 2,
                textTransform: "none",
                color: "#292A2F",
                fontSize: "1rem",
                width:'171px',
                fontWeight: 600,
                bgcolor: "#E9C369",
                borderRadius: "10px",
                height: "48px",
                "&:hover": {
                  bgcolor: "#E9C369",
                  color: "#292A2F",
                },
              }}
            >
              {isLoading ? <CircularProgress size={24} /> : "Submit Request"}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FindJob; 